import { selectIElementsSlice } from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import { RootState } from "@/store/store";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import { areVector3ArraysEqual } from "@/utils/equality";
import {
  getElementTransformation,
  IElementsState,
  isSingleScanSection,
  parseVector3,
  selectAncestor,
} from "@faro-lotv/app-component-toolbox";
import {
  IElement,
  IElementGenericImgSheet,
  IElementImg360,
  IElementTypeHint,
  isIElementSectionGeoslam,
  isIElementSectionRoviScan,
  isIElementSectionWithTypeHint,
} from "@faro-lotv/ielement-types";
import { createSelector } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { Vector3, Vector3Tuple } from "three";
import { DEFAULT_PLACEHOLDER_FLOOR_OFFSET } from "./use-map-placeholder-positions";

const selectPlaceholdersPositions = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: RootState, placeholders: IElementImg360[]) => placeholders,
      (
        state: RootState,
        placeholders: IElementImg360[],
        floor: IElementGenericImgSheet | undefined,
      ) => floor,
      (
        state: RootState,
        placeholders: IElementImg360[],
        floor: IElementGenericImgSheet | undefined,
        shouldUseOriginalPosition: boolean | undefined,
      ) => shouldUseOriginalPosition,
    ],
    (
      iElements: IElementsState,
      placeholders: IElementImg360[],
      floor: IElementGenericImgSheet | undefined,
      shouldUseOriginalPosition: boolean | undefined,
    ): Vector3[] =>
      placeholders.map((placeholder) =>
        parseVector3(
          getPlaceHolderPosition(
            iElements,
            placeholder,
            floor,
            shouldUseOriginalPosition,
          ),
        ),
      ),
  ),
);

/**
 * Calculates the placeholder positions for a given set of IElements in walk mode
 *
 * @param placeholders we want to render
 * @param floor the floor which the placeholders belong to
 * @param shouldUseOriginalPosition if true, the original position of the placeholder will be used
 * @returns the computed list of positions for the placeholders
 */
export function useWalkPlaceholderPositions(
  placeholders: IElementImg360[],
  floor: IElementGenericImgSheet | undefined,
  shouldUseOriginalPosition: boolean | undefined = undefined,
): Vector3[] {
  return useAppSelector(
    selectPlaceholdersPositions(placeholders, floor, shouldUseOriginalPosition),
    areVector3ArraysEqual,
  );
}

/**
 * Calculate elevation of a given the placeholder
 *
 * @param placeholder we want to render
 * @param floor the floor which the placeholders belong to
 * @param shouldUseOriginalPosition if true, the original position of the placeholder will be used
 * @returns the computed placeholder elevation
 */
export function usePlaceholderElevation(
  placeholder: IElementImg360 | undefined,
  floor: IElementGenericImgSheet | undefined,
  shouldUseOriginalPosition?: boolean,
): number | undefined {
  const [elevation, setElevation] = useState<number | undefined>();
  const store = useAppStore();

  useEffect(() => {
    const state = store.getState();
    if (placeholder) {
      setElevation(
        getPlaceHolderPosition(
          state.iElements,
          placeholder,
          floor,
          shouldUseOriginalPosition,
        )[1],
      );
    }
  }, [floor, store, shouldUseOriginalPosition, placeholder]);

  return elevation;
}

/**
 * Compute the adjusted placeholder position to use in walk mode for a set of scans
 * accounting for special heuristics depending on the type of scan
 *
 * @param iElements the iElements slice of the store state
 * @param placeholder the given placeholder
 * @param floor the given floor sheet
 * @param shouldUseOriginalPosition true to use original position
 * @returns the adjusted placeholder position
 */
function getPlaceHolderPosition(
  iElements: IElementsState,
  placeholder: IElementImg360,
  floor: IElementGenericImgSheet | undefined,
  shouldUseOriginalPosition?: boolean,
): Vector3Tuple {
  const { position } = getElementTransformation(iElements, placeholder.id);

  // If the flag is true, then use the original position
  if (shouldUseOriginalPosition) {
    return position;
  }

  const altitude = getScanAltitude(iElements, placeholder);
  if (altitude) {
    return [position[0], position[1] - altitude, position[2]];
  }

  if (floor) {
    // Default: floor height plus offset
    const floorPosition = getElementTransformation(
      iElements,
      floor.id,
    ).position;
    return [
      position[0],
      floorPosition[1] + DEFAULT_PLACEHOLDER_FLOOR_OFFSET,
      position[2],
    ];
  }

  return position;
}

/**
 * @param iElements the iElements slice of the store state
 * @param scan to check the altitude for
 * @returns the altitude from the floor of the passed in scan
 *
 * TODO: generalize the follow up early returns once altitude the info is provided by the backend https://faro01.atlassian.net/browse/SWEB-2533
 */
export function getScanAltitude(
  iElements: IElementsState,
  scan: IElement,
): number | undefined {
  const ORBIS_ALTITUDE_OFFSET = 0.5;
  const FOCUS_ALTITUDE_OFFSET = 1.6;
  if (
    selectAncestor(
      scan,
      (e) =>
        isIElementSectionGeoslam(e) ||
        isIElementSectionWithTypeHint(e, IElementTypeHint.orbisScan) ||
        isIElementSectionWithTypeHint(e, IElementTypeHint.orbisFlashScan) ||
        isIElementSectionRoviScan(e),
    )({ iElements })
  ) {
    return ORBIS_ALTITUDE_OFFSET;
  }
  if (
    selectAncestor(
      scan,
      (el: IElement) =>
        el.typeHint === IElementTypeHint.dataSetWs ||
        el.typeHint === IElementTypeHint.structuredE57 ||
        // Also includes non-focus scans. But we assume that the height is similar, until we have proper metadata
        isSingleScanSection(el),
    )({ iElements })
  ) {
    return FOCUS_ALTITUDE_OFFSET;
  }
}
